import { ReactElement, useEffect, useMemo, useState, useCallback } from 'react';

import { Timestamp } from 'firebase/firestore';

import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';

import EventIcon from '@mui/icons-material/Event';

import dayjs from 'dayjs';

import GratiCsvTable, { GratiTableColumn, GratiTableRow } from '../components/common/GratiCsvTable';
import GratiPageWrapper from '../components/common/GratiPageWrapper';
import EventFileUpload from '../components/event/EventFileUpload';
import LeagueSelector from '../components/league/LeagueSelector';
import { useApp } from '../contextProviders/AppProvider';
import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import useCourses from '../dataHooks/useCourses';
import useSeasons from '../dataHooks/useSeasons';
import { useEventMutators } from '../dataMutators/useEventMutators';
import EventDay from '../types/EventDay';
import League from '../types/League';
import Season from '../types/Season';
import { findSeasonForDate } from '../utils/eventDefaults';

import type Event from '../types/Event';

// Generate day columns for a given day index (0-based).
// Day 1 columns use short names (e.g. "Date"), Day 2+ use "Day N" prefix.
function makeDayColumns(dayIndex: number): Array<GratiTableRow<Event>> {
  const isFirstDay = dayIndex === 0;
  const prefix = isFirstDay ? '' : `Day ${dayIndex + 1} `;
  const shortPrefix = isFirstDay ? '' : `D${dayIndex + 1} `;
  const group = `Day ${dayIndex + 1}`;

  return [
    { field: `${prefix}Date`, required: isFirstDay, dbname: `days[${dayIndex}]._date` as keyof Event, example: '3/15/2025', group },
    { field: `${prefix}Time`, required: false, leagueDefaultKey: 'startTime', dbname: `days[${dayIndex}]._time` as keyof Event, example: '9:00 AM', group },
    { field: `${prefix}Location`, required: false, leagueDefaultKey: 'course', dbname: `days[${dayIndex}]._location` as keyof Event, example: 'Woodlands Golf Course', group },
    { field: `${shortPrefix}Duration`, required: false, leagueDefaultKey: 'duration', dbname: `days[${dayIndex}].duration` as keyof Event, example: '5', group },
    { field: `${shortPrefix}Golf?`, required: false, leagueDefaultKey: 'isGolf', dbname: `days[${dayIndex}].isGolf` as keyof Event, example: 'X', group, excelValidation: { type: 'list', formulae: ['"Yes,No"'] } },
    { field: `${shortPrefix}Shotgun?`, required: false, leagueDefaultKey: 'isShotgun', dbname: `days[${dayIndex}].isShotgun` as keyof Event, example: 'X', group, excelValidation: { type: 'list', formulae: ['"Yes,No"'] } },
    { field: `${shortPrefix}Practice?`, required: false, leagueDefaultKey: 'isPractice', dbname: `days[${dayIndex}].isPractice` as keyof Event, example: 'X', group, excelValidation: { type: 'list', formulae: ['"Yes,No"'] } },
    { field: `${shortPrefix}Num Holes`, required: false, leagueDefaultKey: 'numHoles', dbname: `days[${dayIndex}].numHoles` as keyof Event, example: '18', group, excelValidation: { type: 'list', formulae: ['"9,18"'] } },
    { field: `${shortPrefix}Which 9`, required: false, leagueDefaultKey: 'which9', dbname: `days[${dayIndex}].which9` as keyof Event, example: 'Front', group, excelValidation: { type: 'list', formulae: ['"Front,Back"'] } },
  ];
}

// Event-level columns (not day-specific)
const EVENT_LEVEL_COLUMNS: Array<GratiTableRow<Event>> = [
  { field: 'Name', required: true, dbname: 'name', example: 'Opening Day', group: 'Event' },
  { field: 'Details', required: false, dbname: 'description', example: 'First round!', group: 'Event' },
  { field: 'Num Tee Times', required: false, leagueDefaultKey: 'numTeeTimes', dbname: 'numTeeTimes', example: '6', group: 'Event' },
  { field: 'Num Players', required: false, leagueDefaultKey: 'maxPlayers', dbname: 'maxPlayers', example: '24', group: 'Event', excelFormula: 'C{ROW}*4' },
  { field: 'Team Event?', required: false, dbname: 'isTeamEvent', example: 'X', group: 'Event', excelValidation: { type: 'list', formulae: ['"Yes,No"'] } },
  { field: 'Team Size', required: false, dbname: 'teamStructure.size' as keyof Event, example: '2', group: 'Event' },
  { field: 'Team Composition', required: false, dbname: 'teamStructure.memberGuest' as keyof Event, example: 'Member Only', group: 'Event', excelValidation: { type: 'list', formulae: ['"Member Only,Member/Guest,Guest Only"'] } },
  { field: 'Signup Opens', required: false, leagueDefaultKey: 'signupOpens', dbname: 'signupOpensAt', example: '3/8/2025', group: 'Event' },
  { field: 'Signup Closes', required: false, leagueDefaultKey: 'signupCloses', dbname: 'signupClosesAt', example: '3/13/2025', group: 'Event' },
];

// The template shows all event-level columns, then Day 1 through Day 10.
// The parser supports any number of days dynamically — additional days[N] columns
// in the uploaded file are handled automatically.
const CSV_COLUMNS: Array<GratiTableRow<Event>> = [
  ...EVENT_LEVEL_COLUMNS,    // All event-level columns grouped together
  ...Array.from({ length: 10 }, (_, i) => makeDayColumns(i)).flat(),
];

interface GuideRow {
  property: string;
  [key: string]: string | boolean; // Index signature for dynamic fields
}

// For the Guide view
const GUIDE_COLUMNS: Array<GratiTableColumn<GuideRow>> = [
  { field: 'Property', getValue: (row: GuideRow) => row.property },
  ...CSV_COLUMNS.map((col) => ({
    field: col.field,
    getValue: (row: GuideRow) => row[col.field],
    group: col.group,
    ...(col.excelFormula && { excelFormula: col.excelFormula }),
    ...(col.excelValidation && { excelValidation: col.excelValidation }),
  })),
];

// Static guide rows (League Default row is computed dynamically inside the component)
const GUIDE_ROW_REQUIRED: GuideRow = { property: 'Required', ...CSV_COLUMNS.reduce((acc, col) => ({ ...acc, [col.field]: col.required }), {}) };
const GUIDE_ROW_DBFIELD: GuideRow = { property: 'Database Field', ...CSV_COLUMNS.reduce((acc, col) => ({ ...acc, [col.field]: col.dbname }), {}) };
const GUIDE_ROW_EXAMPLE: GuideRow = { property: 'Example', ...CSV_COLUMNS.reduce((acc, col) => ({ ...acc, [col.field]: col.example }), {}) };

const formatDateTime = (datetime: Timestamp) =>
  `${datetime.toDate().toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  })}\n${datetime.toDate().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })}`;

const formatDate = (datetime: Timestamp) =>
  datetime.toDate().toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });

// For the uploaded Events review view — map parsed Event objects back to display columns.
// Virtual fields (_date, _time, _location) are resolved into real Event/EventDay fields
// by EventFileUpload, so the review table reads the resolved values.
const EVENT_COLUMNS = CSV_COLUMNS.map((guide) => ({
  field: guide.field,
  group: guide.group,
  getValue: (event: Event) => {
    const dayMatch = String(guide.dbname).match(/^days\[(\d+)\]\.(.+)/);
    if (dayMatch) {
      const [, index, field] = dayMatch;
      const day = event.days[parseInt(index)];
      if (!day) return '';

      // Virtual fields show resolved values
      if (field === '_date' || field === '_time') {
        if (day.datetime instanceof Timestamp) {
          return field === '_date'
            ? formatDateTime(day.datetime).split('\n')[0]
            : formatDateTime(day.datetime).split('\n')[1];
        }
        return '';
      }
      if (field === '_location') {
        // Show course name if golf, socialLocation otherwise
        const dayWithDetails = day as EventDay & { courseName?: string; socialLocation?: string };
        return dayWithDetails.courseName || dayWithDetails.socialLocation || '';
      }

      if (field === 'which9') {
        return day.which9 === 'back' ? 'Back' : day.which9 === 'front' ? 'Front' : '';
      }

      const value = day[field as keyof EventDay];
      return (value && (field.includes('datetime') || field.includes('At') || field.includes('Datetime')) && (value instanceof Timestamp) ?
        formatDateTime(value as Timestamp) : String(value ?? ''));
    }

    const value = event[guide.dbname];
    return ((guide.dbname === 'signupOpensAt' || guide.dbname === 'signupClosesAt') && value && value instanceof Timestamp) ?
      formatDate(value as Timestamp) : String(value ?? '');
  },
}));

export default function EventUploadPage(): ReactElement {
  const { leagueId } = useParams<{ leagueId?: string }>();
  const { setPageName } = useApp();
  const { isGod } = useAuth();
  const { canCreateEvents, isLeagueAdmin } = useData();
  const [activeStep, setStep] = useState<'upload' | 'review' | 'save'>('upload');
  const [showDbField, setShowDbField] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedLeague, setSelectedLeague] = useState<League | undefined>(undefined);
  const { addEvent } = useEventMutators();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const navigate = useNavigate();
  const [resetCounter, setResetCounter] = useState(0);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [seasonAssignments, setSeasonAssignments] = useState<Map<number, Season>>(new Map());

  // Load courses for location matching
  const { courses } = useCourses({});

  // Load seasons for auto-resolution by event date
  const { seasons } = useSeasons({
    leagueId: selectedLeague?.itemId,
    orgId: selectedLeague?.orgId,
    includeCatchAll: true,
  });

  const steps = [
    { key: 'upload', label: 'Import CSV or XLSX file' },
    { key: 'review', label: 'Review import' },
    { key: 'save', label: 'Create events' },
  ] as const;

  // Build "Default" guide row dynamically from the selected league
  const leagueDefaultRow = useMemo((): GuideRow => {
    const resolve = (key?: string): string => {
      if (!key) return '';
      switch (key) {
        case 'startTime': {
          const t = selectedLeague?.defaultStartTime;
          return t ? dayjs().hour(t.hour).minute(t.minute).format('h:mm A') : '';
        }
        case 'course':
          return selectedLeague?.defaultCoursePair?.name ?? '';
        case 'maxPlayers':
          return selectedLeague?.defaultMaxPlayers?.toString() ?? '';
        case 'numTeeTimes': {
          const mp = selectedLeague?.defaultMaxPlayers;
          return mp ? Math.ceil(mp / 4).toString() : '';
        }
        case 'signupOpens': {
          const d = selectedLeague?.defaultEventSignupOpensDaysPrior;
          return d !== undefined ? `${d} days before` : '';
        }
        case 'signupCloses': {
          const d = selectedLeague?.defaultEventSignupClosesDaysPrior;
          return d !== undefined ? `${d} days before` : '';
        }
        case 'duration': return '5 hr';
        case 'isGolf': return 'Yes';
        case 'isShotgun': return 'No';
        case 'isPractice': return 'No';
        case 'numHoles': return '18';
        case 'which9': return 'Front (9 holes)';
        default: return '';
      }
    };
    return {
      property: 'Default',
      ...CSV_COLUMNS.reduce((acc, col) => ({
        ...acc,
        [col.field]: resolve(col.leagueDefaultKey),
      }), {}),
    };
  }, [selectedLeague]);

  const guideRows = useMemo(() => {
    const rows = [GUIDE_ROW_REQUIRED, leagueDefaultRow, GUIDE_ROW_DBFIELD, GUIDE_ROW_EXAMPLE];
    return showDbField ? rows : rows.filter((r) => r.property !== 'Database Field');
  }, [showDbField, leagueDefaultRow]);

  useEffect(() => {
    setPageName('Import Events');
  }, [setPageName]);

  const validateUser = () => {
    if (leagueId) {
      return isLeagueAdmin(leagueId) ?? false;
    }
    return canCreateEvents();
  };

  const handleEventsLoaded = useCallback((parsedEvents: Event[]) => {
    setEvents(parsedEvents);
    setSelectedRows(new Set(parsedEvents.map((_, i) => i)));

    // Pre-resolve seasons from event dates
    const assignments = new Map<number, Season>();
    if (seasons) {
      parsedEvents.forEach((event, i) => {
        if (event.startDatetime instanceof Timestamp) {
          const season = findSeasonForDate(dayjs((event.startDatetime as Timestamp).toDate()), seasons);
          if (season) assignments.set(i, season);
        }
      });
    }
    setSeasonAssignments(assignments);
    setStep('review');
  }, [seasons]);

  // When seasons load after events, fill in any unresolved assignments (don't override user changes)
  useEffect(() => {
    if (!seasons || events.length === 0) return;
    setSeasonAssignments((prev) => {
      const updated = new Map(prev);
      let changed = false;
      events.forEach((event, i) => {
        if (!updated.has(i) && event.startDatetime instanceof Timestamp) {
          const season = findSeasonForDate(dayjs((event.startDatetime as Timestamp).toDate()), seasons);
          if (season) {
            updated.set(i, season);
            changed = true;
          }
        }
      });
      return changed ? updated : prev;
    });
  }, [seasons, events]);

  const selectedCount = selectedRows.size;

  // Build review columns: prepend "Create?" checkbox, append "Season" dropdown
  const reviewColumns: GratiTableColumn<Event>[] = useMemo(() => {
    const createCol: GratiTableColumn<Event> = {
      field: 'Create?',
      getValue: () => '',
      isInteractive: true,
      width: 80,
      align: 'center',
      renderCell: (_row: Event, rowIndex: number) => (
        <Checkbox
          size="small"
          checked={selectedRows.has(rowIndex)}
          onChange={(e) => {
            setSelectedRows((prev) => {
              const next = new Set(prev);
              if (e.target.checked) {
                next.add(rowIndex);
              } else {
                next.delete(rowIndex);
              }
              return next;
            });
          }}
        />
      ),
    };

    const seasonCol: GratiTableColumn<Event> = {
      field: 'Season',
      getValue: (row: Event) => {
        const idx = events.indexOf(row);
        return seasonAssignments.get(idx)?.name ?? '';
      },
      isInteractive: true,
      width: 160,
      renderCell: (_row: Event, rowIndex: number) => {
        const current = seasonAssignments.get(rowIndex);
        return (
          <Select
            size="small"
            value={current?.itemId ?? ''}
            onChange={(e) => {
              const season = seasons?.find((s) => s.itemId === e.target.value);
              if (season) {
                setSeasonAssignments((prev) => {
                  const next = new Map(prev);
                  next.set(rowIndex, season);
                  return next;
                });
              }
            }}
            fullWidth
            sx={{ fontSize: '0.75rem' }}
          >
            {seasons?.map((s) => (
              <MenuItem key={s.itemId} value={s.itemId} sx={{ fontSize: '0.75rem' }}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        );
      },
    };

    return [createCol, ...EVENT_COLUMNS, seasonCol];
  }, [selectedRows, seasonAssignments, seasons, events]);

  const handleLeagueChange = (league: League | undefined) => {
    setSelectedLeague(league);
  };

  const handleUpload = async () => {
    setIsUploading(true);

    if (selectedLeague) {
      const leagueId = selectedLeague.itemId;
      const leagueName = selectedLeague.name;
      const orgName = selectedLeague.orgName;

      // Only upload rows the user has checked
      const indicesToUpload = events
        .map((_, i) => i)
        .filter((i) => selectedRows.has(i));

      try {
        for (let j = 0; j < indicesToUpload.length; j++) {
          const i = indicesToUpload[j];
          const season = seasonAssignments.get(i);

          const event = {
            ...events[i],
            leagueId,
            leagueName,
            orgName,
            ...(season && { seasonId: season.itemId, seasonName: season.name }),
          };

          // Clean up course properties from each day — only keep fields defined in EventDay.
          // courseName/courseShortName are used for coursePairs fixup but shouldn't persist on the day.
          if (event.days) {
            event.days = event.days.map(day => {
              const cleanDay: EventDay = {
                courseId: day.courseId,
                datetime: day.datetime,
                duration: day.duration,
                isGolf: day.isGolf,
                isShotgun: day.isShotgun,
                isPractice: day.isPractice,
                numHoles: day.numHoles,
                ...(day.socialLocation ? { socialLocation: day.socialLocation } : {}),
              };
              return cleanDay;
            });
          }

          await addEvent(event);
          setUploadProgress(((j + 1) / indicesToUpload.length) * 100);
        }
        // Success - redirect or show message
        navigate('/events');
      } catch (error) {
        console.error('Error uploading events:', error);
        // Show error message
      } finally {
        setIsUploading(false);
      }
    } else {
      console.error('Error uploading events: No league selected');
      // Show error message
    }
  };

  const handleClear = () => {
    setEvents([]);
    setSelectedRows(new Set());
    setSeasonAssignments(new Map());
    setStep('upload');
    setResetCounter((prev) => prev + 1); // Force reset of upload component
  };

  return (
    <GratiPageWrapper validateUser={validateUser}>
      <Stack spacing={2}>
        <Stepper activeStep={steps.findIndex((s) => s.key === activeStep)} sx={{ mb: 4 }}>
          {steps.map((step) => (
            <Step key={step.key}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {leagueId && selectedLeague ? (
          <Typography variant="bodyLarge">
            Import events into the <strong>{selectedLeague.name}</strong>
            {!/league$/i.test(selectedLeague.name) && ' league'}.
          </Typography>
        ) : (
          <Typography variant="bodyLarge">Select a league to import events into.</Typography>
        )}

        <LeagueSelector
          value={selectedLeague?.itemId ?? leagueId ?? ''}
          showOrgNames={true}
          onChange={handleLeagueChange}
          sx={leagueId ? { display: 'none' } : undefined}
        />

        {/* Season is auto-resolved from each event's date during upload */}

        {activeStep === 'upload' && (
          <>
            {isGod && (
              <FormControlLabel
                control={<Checkbox checked={showDbField} onChange={(e) => setShowDbField(e.target.checked)} size="small" />}
                label="Show database header row"
              />
            )}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="titleMedium" gutterBottom>
                  Event template
                </Typography>
                <GratiCsvTable<GuideRow>
                  columns={GUIDE_COLUMNS}
                  rows={guideRows}
                  description="Your spreadsheet (CSV or XLSX) can have the following columns:"
                  hasDownloadAction={true}
                />
              </CardContent>
            </Card>
          </>
        )}

        {activeStep === 'review' && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="titleMedium" gutterBottom>
                Review upload
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Review each event for accuracy. You can remove a row from event creation by unchecking
                the &ldquo;Create?&rdquo; checkbox.{' '}
                {selectedCount > 0
                  ? <>When you are satisfied, tap the &ldquo;Create {selectedCount}{' '}
                    event{selectedCount !== 1 ? 's' : ''}&rdquo; button to add the events to the league.</>
                  : <>At least one &ldquo;Create?&rdquo; checkbox must be selected to create events.</>
                }
              </Typography>
              <GratiCsvTable
                columns={reviewColumns}
                rows={events}
                isEditable={true}
                onRowChange={(rowIndex, updatedEvent) => {
                  setEvents((prev) => prev.map((event, i) => (i === rowIndex ? updatedEvent : event)));
                }}
              />

              <Stack
                direction="row"
                spacing={2}
                sx={{
                  justifyContent: "flex-end",
                  mt: 2
                }}>
                {events?.length > 0 && (
                  <Button variant="outlined" size="small" onClick={handleClear}>
                    Start over
                  </Button>
                )}
                <Button
                  loading={isUploading}
                  loadingPosition="start"
                  size="small"
                  startIcon={<EventIcon />}
                  variant="contained"
                  onClick={handleUpload}
                  disabled={selectedCount === 0}
                >
                  Create {selectedCount} event{selectedCount !== 1 ? 's' : ''}
                </Button>
              </Stack>

              {isUploading && <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />}
            </CardContent>
          </Card>
        )}

        {selectedLeague && activeStep === 'upload' && (
          <EventFileUpload
            league={selectedLeague}
            season={undefined}
            courses={courses ?? []}
            mapping={CSV_COLUMNS}
            hasImportAction={true}
            onEventsLoaded={handleEventsLoaded}
            resetKey={resetCounter}
          />
        )}
      </Stack>
    </GratiPageWrapper>
  );
}
